<template>
  <div>
    <div
      ref="el"
      v-bind="$attrs"
      style="
        border-radius: 8px;
        border: 1px solid rgba(0, 0, 0, 0.05);
        width: fit-content;
        font-size: 0.9rem;
        max-width: 500px;
        min-width: 500px;
        padding: 32px 0px 16px 0px;
        background-color: white;
      "
    >
      <header
        style="height: auto; font-size: 0.8rem; padding: 0px 16px; display: flex; min-height: 75px"
      >
        <div style="width: 200px; position: relative; flex-grow: 1">
          <div style="position: absolute; inset: 0; height: 100%; padding-bottom: 3px">
            <img :src="rxImage" style="height: 100%" />
          </div>
        </div>
        <section style="line-height: 1.4" class="text-right">
          <div>120-115 Christopher Drive</div>
          <div>Cambridge, Ontario N1R 4S1</div>
          <div>Phone: 705-328-2019</div>
          <div>Fax: 705-328-1842</div>
        </section>
      </header>
      <v-divider style="margin: 16px 0px" />
      <div style="padding: 0px 16px; display: flex; font-size: 0.8rem">
        <section v-if="member">
          <div>{{ $t('format.fullName', member) }}</div>
          <div>DOB: {{ $store.getters.toTimezone(member.dateOfBirth).format('YYYY-MM-DD') }}</div>
          <div>HCN: {{ hcn }}</div>
        </section>
        <section style="flex-grow: 1">
          <div style="text-align: right; font-weight: 600">
            {{ $store.getters.toTimezone().format('MMM Do YYYY') }}
          </div>
        </section>
      </div>
      <v-divider style="margin: 16px 0px" />
      <section style="font-size: 0.8rem; padding: 0px 16px" v-if="!isManual || saving">
        <div
          style="
            background-color: #f9fafb;
            border-radius: 8px;
            white-space: pre-line;
            line-height: 1.4;
            padding: 16px;
          "
          data-testid="prescription-pad__text"
        >
          {{ prescriptionText }}
        </div>
      </section>

      <section style="padding: 16px 0px 32px 16px">
        <div
          style="display: grid; grid-template-columns: auto 1fr; grid-template-rows: repeat(2, 1fr)"
        >
          <div style="padding-top: 16px; padding-left: 16px">Dr.Tuaha Saeed</div>
          <div />

          <div style="padding-left: 16px">#053641</div>
        </div>
        <div style="padding-left: 16px">
          <img :src="signature" style="width: 150px" />
        </div>
      </section>
      <footer style="font-size: 0.6rem; padding: 16px; color: var(--sentry-gray-500)">
        {{ $store.getters.toTimezone().format('MMM Do YYYY') }}
      </footer>
    </div>
  </div>
</template>
<style>
/* This component must only use inline css styles to be properly rendered by html2pdf */
.prescription-textarea textarea {
  line-height: 1.4 !important;
}
</style>
<script>
/**
 * DEVELOPER NOTES:
 * This component must only be used by creating a new instance of it via createPrescriptionPadInstance function
 * exported in the root index.ts file in the PrescriptionPad directory.
 *
 * For example of Usage, see the PrescriptionEditor.vue file in the Prescriptions directory.
 */
import html2PDF from 'html2pdf.js';
import rxImage from './Prescription/rx-image-base64';
import signature from './Prescription/signature-base64';
import { formatHCN } from '../utils/healthcard';
import { uploadPatientFile } from '../utils/file-upload';
import { generateUniqueId } from '../utils/id';

export default {
  props: {
    employee: Object,
    partnerId: String,
    isManual: Boolean,
    saving: Boolean,
  },

  data() {
    return {
      cpsoNumber: '',
      maxLengthRule: [() => 'Maximum 500 characters allowed'],
      rxImage,
      signature,
    };
  },
  computed: {
    isReturningPatient() {
      return this.$store.state.registration.isReturningPatient;
    },
    member() {
      return this.$store.state.registration.patientData;
    },
    prescriptionText() {
      if (this.isReturningPatient) return '';
      if (this.isExistingMedication) {
        const { existingMedication } = this;
        const [type, dose] = existingMedication.split('_');
        const getExistingMedicationPrescription = (medType, medDose) => {
          return (
            {
              ozempic: `
        Semaglutide ${medDose}mg SC once weekly x 4 weeks. 
        Refills: 1
        `,
              tirzepatide: `
          Tirzepatide ${medDose}mg SC once weekly x 4 weeks. 
          Refills: 1
        `,
              liraglutide: `
        Liraglutide ${medDose}mg SC once daily x 4 weeks. 
        Refills: 1
        `,
              rybelsus: `
          Semaglutide ${medDose}mg PO once daily x 30 days
          Refills: 0
        `,
            }[medType] || null
          );
        };
        const e = getExistingMedicationPrescription(type, dose);

        if (e) return e;
      }

      return {
        Semaglutide: `
        Semaglutide 0.25mg SC once weekly x 4 weeks. 
        May increase to Semaglutide 0.5mg SC once weekly after 4-8 weeks.
        Refills: 1
        `,
        Tirzepatide: `
          Tirzepatide 2.5mg SC once weekly x 4 weeks. 
          May increase to Tirzepatide 5mg SC once weekly after 4-8 weeks.
          Refills: 1
        `,
        Liraglutide: `
        Liraglutide 0.6mg SC once daily x 4 weeks. 
        May increase to Liraglutide 1.2mg SC once daily after 1-2 weeks.
        Refills: 1
        `,
        Rybelsus: `
          Semaglutide 3mg PO once daily x 30 days
          Refills: 0
        `,
      }[this.selectedMed];
    },
    selectedMed() {
      return (
        this.$store.state.registration.surveyAnswers.rx_choice_detail || 'Semaglutide'
      ).trim();
    },
    existingMedication() {
      return this.$store.state.registration.surveyAnswers.current_medication;
    },
    isExistingMedication() {
      return (
        this.$store.state.registration.surveyAnswers.rx_choice_detail === 'existing medication'
      );
    },
    hcn() {
      const { healthCard } = this.member || {};
      if (!healthCard) return '';
      return formatHCN(healthCard);
    },
    memberId() {
      return this.$store.state.registration.memberId;
    },
  },
  methods: {
    async createPrescription() {
      const file = await this.generatePdf();
      await uploadPatientFile({
        file,
        memberId: this.memberId,
        tags: ['TRUFORME'],
        metaData: [
          ...(this.partnerId ? [{ key: 'pendingPartnerId', valueString: this.partnerId }] : []),
        ],
        throwError: false,
        unlock: false,
      });
      this.$emit('success');
    },
    async generatePdf() {
      const { el: pres } = this.$refs;
      pres.classList.remove('component-slide-enter');
      pres.classList.remove('component-slide-enter-active');
      const presClone = pres.cloneNode(true);

      presClone.id = generateUniqueId();
      const el = document.createElement('div');
      el.style.width = '8.3in';
      el.style.height = '11.7in';
      el.style.margin = '0px auto';
      el.style.position = 'relative';
      el.style.overflow = 'hidden';
      el.style.display = 'flex';
      el.style.justifyContent = 'center';
      el.style.alignItems = 'center';

      el.appendChild(presClone);
      document.body.appendChild(el);

      const output = await html2PDF()
        .set({
          jsPDF: { unit: 'mm', format: 'A4', orientation: 'p' },
          html2canvas: {
            useCORS: true,
            allowTaint: true,
            x: 0,
            y: 0,

            windowWidth: 768,
            windowHeight: 600,
            dpi: 300,
            scale: 1,
            letterRendering: true,
          },
          margin: 0,
          image: {
            type: 'jpeg',
            quality: 0.98,
          },
        })
        .from(el, 'element')
        .toPdf()
        .get('pdf')
        .catch((e) => {
          console.log('error generating pdf: ', e);
        });
      // output.save();
      const blob = output.output('blob');

      const file = new File(
        [blob],
        `${this.selectedMed?.toUpperCase() || ''}-${this.$store.getters
          .toTimezone()
          .format('YYYY-MM-DD')}.pdf`,
        { type: 'application/pdf' }
      );
      el.remove();
      return file;
    },
  },
  mounted() {
    setTimeout(() => {
      this.createPrescription();
    }, 100);
  },
};
</script>
